import { ConversionAvailabilityRow, RevenueRow } from "@/http/api-types";

export interface AggregatedRevenueData {
  revenue_eur: number;
  ap_conversions: number;
  secondary_ap_conversions: number;
  ap_target_cpx_eur: number;
  secondary_ap_target_cpx_eur: number;
  cpi: number;
  secondary_cpa: number;
  cpi_achievement: number;
  secondary_cpa_achievement: number;
}

export interface AvailabilityStats {
  avgAvailability: number;
  totalConversions: number;
}

export function calculateRevenueAggregates(data: RevenueRow[]): AggregatedRevenueData | null {
  if (!data.length) return null;

  const totalRevenue = data.reduce((sum, row) => sum + row.revenue_eur, 0);
  const totalApConversions = data.reduce((sum, row) => sum + row.ap_conversions, 0);
  const totalSecondaryApConversions = data.reduce((sum, row) => sum + row.secondary_ap_conversions, 0);
  const totalApTargetCpx = data.reduce((sum, row) => sum + row.ap_target_cpx_eur * row.ap_conversions, 0);
  const totalSecondaryApTargetCpx = data.reduce(
    (sum, row) => sum + row.secondary_ap_target_cpx_eur * row.secondary_ap_conversions,
    0,
  );

  return {
    revenue_eur: totalRevenue,
    ap_conversions: totalApConversions,
    secondary_ap_conversions: totalSecondaryApConversions,
    ap_target_cpx_eur: totalApTargetCpx / Math.max(totalApConversions, 1),
    secondary_ap_target_cpx_eur: totalSecondaryApTargetCpx / Math.max(totalSecondaryApConversions, 1),
    cpi: totalApConversions > 0 ? totalRevenue / totalApConversions : 0,
    secondary_cpa: totalSecondaryApConversions > 0 ? totalRevenue / totalSecondaryApConversions : 0,
    cpi_achievement: totalApTargetCpx > 0 ? totalRevenue / totalApTargetCpx : 0,
    secondary_cpa_achievement: totalSecondaryApTargetCpx > 0 ? totalRevenue / totalSecondaryApTargetCpx : 0,
  };
}

export function calculateCPIAchievement(data: RevenueRow[]): number {
  const aggregates = calculateRevenueAggregates(data);
  return aggregates?.cpi_achievement || 0;
}

export function calculateSecondaryCPAAchievement(data: RevenueRow[]): number {
  const aggregates = calculateRevenueAggregates(data);
  return aggregates?.secondary_cpa_achievement || 0;
}

export function calculateTotalRevenue(data: RevenueRow[]): number {
  return data.reduce((sum, row) => sum + row.revenue_eur, 0);
}

export function calculateAvailabilityStats(
  data: ConversionAvailabilityRow[],
  type: "primary" | "secondary",
): AvailabilityStats {
  if (!data.length) return { avgAvailability: 0, totalConversions: 0 };

  const totalField = type === "primary" ? "ap_conversions_total" : "secondary_ap_conversions_total";
  const withBidTsField = type === "primary" ? "ap_conversions_with_bid_ts" : "secondary_ap_conversions_with_bid_ts";

  const totalConversions = data.reduce((sum: number, row: any) => sum + row[totalField], 0);
  const totalWithBidTs = data.reduce((sum: number, row: any) => sum + row[withBidTsField], 0);
  const avgAvailability = totalConversions > 0 ? (totalWithBidTs / totalConversions) * 100 : 0;

  return { avgAvailability, totalConversions };
}
